import tw from 'twin.macro'
import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import * as Accordion from '@radix-ui/react-accordion'
import { FiCopy } from 'react-icons/fi'
import { CgChevronDown, CgAdd, CgInfo } from 'react-icons/cg'
import { isEqual, pick } from 'lodash'
import { useForm, FormProvider } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import TagInput from 'common/components/Tag/TagInput'

import {
  useUpdateExerciseMutation,
  useRemoveExerciseMutation,
  useListenExercisesQuery,
  useListenExLibVisibilityQuery,
  useListenExerciseQuery,
} from '../exerciseLibraryApi'
import { useListenShowProgressionFilterQuery } from 'modules/ContentTags/contentTagsApi'
import { useSaveProgressionMutation } from 'modules/Progressions/progressionsApi'
import { useDialog } from 'common/components/Dialog/hooks/useDialog'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { useCustomization } from 'common/contexts/Customization/useCustomization'
import { ROUTES } from 'modules/App/AppRouter'

import { schema } from 'modules/ExerciseLibrary/ExerciseForm/schema'
import { Input } from 'common/components/Input/Input'
import { CueInputList } from 'common/components/CueInput/CueInputList'
import { UpdateActions } from 'common/components/UpdateActions/UpdateActions'
import { DeleteConfirmationBanner } from 'common/components/DeleteConfirmationBanner/DeleteConfirmationBanner'
import { ExerciseProgressionAccordion } from './ExerciseProgressionAccordion'
import { UploadInput } from 'common/components/UploadInput/UploadInput'
import { createUID } from 'common/utils/createUID'
import { getRepDurationFromName } from 'common/utils/wktDurationUtils'
import { roundDurationString } from 'common/utils/timeDuration'
import { removeFileExtension } from 'common/utils/fileUploading/nameAndIdUtils'
import { getCustomPreviewUrl, trimAndRemoveEmptyValues, capsFirstLetter } from './utils'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import AddUnitButton, { getAdjustedUnit } from 'common/components/AddUnitButton/AddUnitButton'
import Tag from 'common/components/Tag/Tag'
import Time from 'common/components/Time/Time'
import { exerciseSelected } from '../exerciseLibrarySlice'
import { exerciseNameChanged, uploadingExerciseCancelled } from '../uploadingExercisesSlice'
import { useEventListener } from 'common/hooks/useEventListener'
import { getLandOrPortExThumb, getLandOrPortExVidForPlayer } from 'common/utils/exerciseUtils'
import { exLibDisplayValues } from 'common/constants/exLibConstants'
import { VID_FILESIZE_LIMIT } from 'common/utils/fileUploading/validators'

const userControlledExValues = [
  'name',
  'level',
  'cues',
  'tags',
  'units',
  'timeEstimatePerRep',
  'timeEstimatePerRepSource',
]

function ExerciseForm({ coachOrgId, exercise = {}, exerciseKey, progressions, exerciseList }) {
  const { exVidOrientation } = useCustomization()
  const [updateExercise] = useUpdateExerciseMutation()
  const [removeExercise] = useRemoveExerciseMutation()
  const [saveProgression] = useSaveProgressionMutation() //Need to update progerssion if exercise is deleted
  const [accordionValue, setAccordionValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const [, setDialogOpen] = useDialog()
  const { createAlert } = useAlert()
  const dispatch = useDispatch()

  const isNewExercise = !exerciseKey
  const [exerciseId, setExerciseId] = useState(null)

  const { data: ex } = useListenExerciseQuery({ coachOrgId, exerciseId })
  const thumbnail = getLandOrPortExThumb(exVidOrientation, ex)

  const { data: showProgressionFilter } = useListenShowProgressionFilterQuery({ coachOrgId })
  const { data: exLibVisibilityData } = useListenExLibVisibilityQuery({ coachOrgId })
  const showProgressionFilterLoading = showProgressionFilter === undefined || showProgressionFilter?.isLoading
  const exLibEnabledLoading = exLibVisibilityData === undefined || exLibVisibilityData?.isLoading
  const exLibEnabled = Boolean(exLibVisibilityData) && exLibVisibilityData !== exLibDisplayValues.hidden
  const showLevelAndTags =
    !showProgressionFilterLoading && !exLibEnabledLoading && (showProgressionFilter === true || exLibEnabled)

  const currentExerciseProgression =
    progressions !== null ? Object.entries(progressions).find(([_key, value]) => value.includes(exerciseKey)) : []
  const [progressionKey, progressionExercises] = currentExerciseProgression || []
  const progressionExercisesRef = useRef(progressionExercises || []) // Store in ref to have stable default value through component lifecycle

  const [isCreated, setIsCreated] = useState(false) //set if exercise is created by uploading asset

  const { data: exData } = useListenExercisesQuery({ coachOrgId, exVidOrientation })
  const tagsList = exData?.isFiller || exData === undefined ? [] : exData.tagsList

  useEffect(() => {
    if (!isNewExercise) {
      setExerciseId(exerciseKey)
    } else {
      const newId = createUID()
      const lowercaseNewId = newId.toLowerCase() //Temporary, mobile app cannot intrepet if not lowercase
      setExerciseId(`${lowercaseNewId}-`) //Arbitary "-" added as the letter "s" cannot be interpretted properly by app
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultValues = {
    name: exercise.name || '',
    level: exercise.level ? String(exercise.level) : '', //Need to parse to string in order for isDirty to work properly
    cues: exercise.cues || [],
    tags: exercise.tags || [],
    exercises: progressionExercisesRef.current,
    units: exercise?.units || [],
    // Rounded because a stored value can carry fractional seconds while the form's time input
    // holds whole ones. Without this, the input's value differs from the default the moment the
    // form opens, and the source comparison on save mistakes that for a coach edit.
    timeEstimatePerRep: roundDurationString(exercise?.timeEst?.perRep) || '00:00:00.00',
    timeEstimatePerRepSource: exercise?.timeEst?.source || null,
  }
  const methods = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    context: {
      progressions,
      progressionKey: progressionKey,
      exerciseList,
    },
  })

  const {
    watch,
    setValue,
    setFocus,
    setError,
    clearErrors,
    register,
    formState: { errors },
    handleSubmit,
  } = methods

  const formState = watch()

  // What we filled in ourselves, so renames keep updating it while a coach's own figure is never
  // overwritten. Seeded from a saved 'ai' duration, since no coach-entered figure is stored under
  // that source.
  const autoFilledTimeRef = useRef(
    defaultValues.timeEstimatePerRepSource === 'ai' ? defaultValues.timeEstimatePerRep : null
  )

  // Only a rename should revisit the figure. Seeded with the saved name so that merely opening an
  // exercise never clears a saved duration its name does not state.
  const lastHandledNameRef = useRef(defaultValues.name)

  useEffect(() => {
    if (formState.name === lastHandledNameRef.current) return
    lastHandledNameRef.current = formState.name

    const currentTime = formState.timeEstimatePerRep || '00:00:00.00'
    const isCoachEdited = currentTime !== '00:00:00.00' && currentTime !== autoFilledTimeRef.current
    if (isCoachEdited) return

    const derivedTime = getRepDurationFromName(formState.name)
    const nextTime = derivedTime || '00:00:00.00'
    if (nextTime === currentTime) return

    autoFilledTimeRef.current = derivedTime
    setValue('timeEstimatePerRep', nextTime)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.name])

  const onSubmit = async (data) => {
    const exerciseData = pick(data, userControlledExValues) //only include data that can be altered by ExerciseForm

    const alertText = isNewExercise && !isCreated ? 'Exercise created!' : 'Exercise updated!'
    setLoading(true)
    // const firebaseValidName = removeInvalidFirebaseChars(exerciseData.name)
    // const uploadExKey = isNewExercise ? (firebaseValidName + exerciseId).toLowerCase() + '-' : exerciseId //Have to lowercase entries because mobile-app does not properly match to capital letters right now
    const uploadExKey = exerciseId
    //Arbitrary "-" added to upload key save due to potential issues with mobile app parsing name function

    exerciseData.cues = trimAndRemoveEmptyValues(exerciseData.cues)
    exerciseData.cues = capsFirstLetter(exerciseData.cues)
    const progressionExercises = trimAndRemoveEmptyValues(data.exercises)
    const timeEstimatePerRep =
      !exerciseData?.timeEstimatePerRep || exerciseData?.timeEstimatePerRep === '00:00:00.00'
        ? null
        : exerciseData?.timeEstimatePerRep

    // The backend only revisits exercises whose source is 'ai'; anything else it treats as the
    // coach's choice. Claiming 'coach' for a figure no coach typed would lock it in forever.
    const isDerivedFromName = timeEstimatePerRep && timeEstimatePerRep === autoFilledTimeRef.current

    let timeEstimatePerRepSource
    if (!timeEstimatePerRep) {
      timeEstimatePerRepSource = null
    } else if (isDerivedFromName) {
      timeEstimatePerRepSource = 'ai'
    } else if (defaultValues.timeEstimatePerRep !== formState.timeEstimatePerRep) {
      timeEstimatePerRepSource = 'coach'
    } else {
      timeEstimatePerRepSource = formState.timeEstimatePerRepSource || 'coach'
    }

    await updateExercise({
      coachOrgId,
      exerciseKey: uploadExKey,
      exercise: {
        ...exerciseData,
        timeEstimatePerRep: null,
        timeEstimatePerRepSource: null,
        timeEst: {
          perRep: timeEstimatePerRep,
          source: timeEstimatePerRepSource,
        },
        updatedAt: Date.now(),
      },
    })

    dispatch(exerciseNameChanged({ exerciseId: exerciseKey, name: exerciseData.name }))

    if (progressionKey) {
      await saveProgression({ coachOrgId, progressionKey, exercises: progressionExercises })
    }
    setLoading(false)
    createAlert({ text: alertText, type: 'success' })
    setDialogOpen(false)
  }

  const handleDelete = async () => {
    setLoading(true)
    await removeExercise({ coachOrgId, exerciseKey })
    if (progressionKey) {
      const updatedExercises = progressionExercises.filter((exercise) => exercise !== exerciseId)
      await saveProgression({
        coachOrgId,
        progressionKey: progressionKey,
        exercises: updatedExercises,
      })
    }
    dispatch(uploadingExerciseCancelled({ exerciseId: exerciseKey }))
    dispatch(exerciseSelected({ exId: exerciseKey }))
    setLoading(false)
    createAlert({ text: 'Exercise deleted!', type: 'success' })
    setDialogOpen(false)
  }

  const isFormDisabled = isEqual(pick(formState, userControlledExValues), pick(defaultValues, userControlledExValues))
  const isInProgression = !isNewExercise && progressionKey

  const createExerciseOnUpload = (event) => {
    if (isNewExercise) {
      const file = event.target.files[0]
      if (file && file.name) {
        const exName = formState.name || removeFileExtension(file.name)
        const uploadExKey = exerciseId
        const exData = {
          name: exName,
        }

        updateExercise({
          coachOrgId,
          exerciseKey: uploadExKey,
          exercise: { ...exData, updatedAt: Date.now() },
        })
        const alertText = 'Creating exercise...'
        createAlert({ text: alertText, type: 'success' })
        setIsCreated(true)
      }
    }
  }

  // refs control
  const { moveFocusedInputTo, moveFocusedInputBy, moveFocusOnKeyPress, addInputRef } = useFormRefsControl()
  const nameRef = useRef()
  const tagRef = useRef()
  const levelRef = useRef()
  const minsRef = useRef()
  const secsRef = useRef()

  const submitRef = useRef()
  const inputRefsSortMethod = ['name', 'cue', 'exercise', 'progression', 'tag', 'level', 'minutes', 'seconds', 'submit']

  useEffect(() => {
    if (!showProgressionFilterLoading && !exLibEnabledLoading) {
      addInputRef({ ref: nameRef, posIdx: 0, name: 'name', sortMethod: inputRefsSortMethod })
      if (showLevelAndTags) {
        addInputRef({
          ref: tagRef,
          posIdx: formState.cues.length + formState.exercises.length + 1,
          name: 'tag',
          sortMethod: inputRefsSortMethod,
        })
        addInputRef({
          ref: levelRef,
          posIdx: formState.cues.length + formState.exercises.length + 2,
          name: 'level',
          sortMethod: inputRefsSortMethod,
        })
      }
      addInputRef({
        ref: minsRef,
        posIdx: formState.cues.length + formState.exercises.length + showLevelAndTags ? 4 : 2,
        name: 'minutes',
        sortMethod: inputRefsSortMethod,
      })
      addInputRef({
        ref: secsRef,
        posIdx: formState.cues.length + formState.exercises.length + showLevelAndTags ? 5 : 3,
        name: 'seconds',
        sortMethod: inputRefsSortMethod,
      })
      addInputRef({
        ref: submitRef,
        posIdx: formState.cues.length + formState.exercises.length + showLevelAndTags ? 6 : 4,
        name: 'submit',
        sortMethod: inputRefsSortMethod,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProgressionFilterLoading, exLibEnabledLoading])

  const maybeOpenAndFocusProgressions = () => {
    if (accordionValue !== 'Progressions') {
      setAccordionValue('Progressions')

      const exercises = formState.exercises
      if (exercises.length > 0) {
        const isLastExEmptyStr = exercises[exercises.length - 1]?.trim() === ''
        // Need to make sure second to last progression exercise input is selected
        // in case the last input value is an empty string that was previously added by tabbing or button
        // because empty string remains in form state while closing/reopening accordion, but input itself is removed
        const lastExInputIdx = isLastExEmptyStr && exercises.length > 1 ? exercises.length - 2 : exercises.length - 1
        // Timeout is needed so that input is focused after progressions accordion is opened
        setTimeout(() => {
          setFocus(`exercises.${lastExInputIdx}`)
        }, 50)
      }
    }
  }

  const moveNameFocusOnKeyPress = (e) => {
    if (e.code === 'Enter' || e.code === 'Tab') {
      e.preventDefault()
      e.stopPropagation()
      if (e.shiftKey) {
        if (isFormDisabled && !exLibEnabled) {
          maybeOpenAndFocusProgressions()
        } else {
          moveFocusedInputBy(-1)
        }
      } else if (!e.shiftKey) {
        if (accordionValue !== 'formCues') {
          setAccordionValue('formCues')
        } else {
          moveFocusedInputBy(1)
        }
      }
    }
  }

  const moveTagFocusOnKeyPress = (e) => {
    if (e.code === 'Enter' || e.code === 'Tab') {
      e.preventDefault()
      e.stopPropagation()

      const isTagInputEmpty = e.target.value.trim() === ''
      if (e.code === 'Enter' || (!isTagInputEmpty && e.code === 'Tab')) {
        return
      }

      if (e.shiftKey) {
        maybeOpenAndFocusProgressions()
        moveFocusedInputBy(-1)
      } else {
        moveFocusedInputBy(1)
      }
    }
  }

  const copyCues = async (e) => {
    e.preventDefault()
    const toCopy = formState.cues.join('\n')
    await navigator.clipboard.writeText(toCopy || '')
    createAlert({ text: 'Cues copied!', type: 'success' })
  }

  useEventListener('keydown', (e) => {
    if (e.code === 'Tab') e.preventDefault()
  })

  return (
    <FormProvider {...methods}>
      <div className='divide-y divide-gray-200 overflow-auto'>
        <form
          onSubmit={handleSubmit(onSubmit)}
          id='exerciseForm'
          name='Exercise Form'
          onKeyDown={(e) => {
            if (e.code === 'Enter' || e.code === 'Tab') {
              e.preventDefault() //Otherwise form autosubmits on each enter press
            }
          }}
        >
          <div className='flex flex-col px-4 md:px-10 py-4 border-b border-gray-200'>
            <ExerciseNameInput
              register={register}
              inputRef={nameRef}
              errors={errors}
              onKeyDown={(e) => {
                // When form is disabled and trying to use Tab to open formCues accordion onKeyUp is not firing
                // Opening accordion onKeyDown works
                if (
                  e.code === 'Tab' &&
                  !e.shiftKey &&
                  accordionValue !== 'formCues' &&
                  isFormDisabled &&
                  !exLibEnabled
                ) {
                  setAccordionValue('formCues')
                  if (formState.cues.length === 0) {
                    setTimeout(() => {
                      moveFocusedInputBy(1)
                    }, 50)
                  }
                }
              }}
              onKeyUp={moveNameFocusOnKeyPress}
              autoFocus={true}
            />
          </div>
          <div className='flex flex-col px-4 md:px-10 py-4 border-b border-gray-200'>
            <UploadInput
              name='video'
              liveUrl={
                getLandOrPortExVidForPlayer(exVidOrientation, ex) ||
                getLandOrPortExVidForPlayer(exVidOrientation, exercise)
              }
              previewUrl={getCustomPreviewUrl(exerciseId)}
              id={exerciseId}
              coachOrgId={coachOrgId}
              register={register}
              setValue={setValue}
              label='Video'
              uploadType='update-exercise-video'
              setError={setError}
              clearErrors={clearErrors}
              onUpload={isNewExercise ? createExerciseOnUpload : null}
              uploadDisabled={!formState.name || formState?.name?.length < 3}
              fileType='video'
              customFileSizeLimit={VID_FILESIZE_LIMIT}
              labelDescription={`${VID_FILESIZE_LIMIT / 1000000}MB max file size`}
              orientation={exVidOrientation}
            />
            <UploadInput
              name='thumbnail'
              liveUrl={thumbnail || getLandOrPortExThumb(exVidOrientation, exercise)}
              id={exerciseId}
              coachOrgId={coachOrgId}
              register={register}
              setValue={setValue}
              hidden
              uploadType='update-exercise-thumbnail'
              setError={setError}
              clearErrors={clearErrors}
              uploadDisabled={!formState.name}
              fileType='image'
            />
            {/* <UploadInput
              name='audio'
              liveUrl={audio || exercise?.audio}
              id={exerciseId}
              coachOrgId={coachOrgId}
              register={register}
              setValue={setValue}
              uploadType='update-exercise-audio'
              setError={setError}
              clearErrors={clearErrors}
              uploadDisabled={!formState.name}
              fileType='audio'
            /> */}
            {errors?.video ? <InputError>{errors.video.message}</InputError> : null}
            {/* {errors?.audio ? <InputError>{errors.audio.message}</InputError> : null} */}
          </div>
          <CueInputAccordion
            accordionValue={accordionValue}
            setAccordionValue={setAccordionValue}
            copyCues={copyCues}
            haveCues={Boolean(formState.cues.length)}
            inputRefsSortMethod={inputRefsSortMethod}
          />
          {!isNewExercise && (
            <ExerciseProgressionAccordion
              coachOrgId={coachOrgId}
              exerciseKey={exerciseKey}
              isInProgression={isInProgression}
              progressions={progressions}
              progressionKey={progressionKey}
              exerciseList={exerciseList}
              accordionValue={accordionValue}
              setAccordionValue={setAccordionValue}
              inputRefsSortMethod={inputRefsSortMethod}
              autoFocus={true}
              setValue={setValue}
            />
          )}
          {showLevelAndTags && (
            <div className='flex flex-col px-4 md:px-10 py-4 border-t border-gray-200'>
              <label className='capitalize inline-flex font-semibold text-tBlack mb-1'>Exercise Library</label>
              <p className='text-xs text-gray-500 mb-2'>
                {formState.tags?.length > 0
                  ? 'Include at least 1 tag to continue displaying this exercise in the Exercise Library'
                  : 'Add a tag to show this exercise in the Exercise Library'}
                <Tooltip
                  content={
                    <div>
                      You can set whether the Exercise Library is visible in{' '}
                      <Link to={`/${ROUTES.ADVANCED}`} className='underline'>
                        Advanced Settings
                      </Link>
                    </div>
                  }
                  triggerClasses='ml-1 text-gray-500 hover:text-tGreen cursor-pointer inline-block relative top-[2px]'
                >
                  <CgInfo className='w-3.5 h-3.5' />
                </Tooltip>
              </p>
              <TagInput
                activeTags={formState.tags}
                tagsList={tagsList}
                setActiveTags={(value) => setValue('tags', value)}
                register={register}
                controlledInputRef={tagRef}
                onKeyUp={moveTagFocusOnKeyPress}
              />
              <div className='border-t border-gray-200 mt-2 mb-1' />
              <LevelInput register={register} inputRef={levelRef} errors={errors} onKeyUp={moveFocusOnKeyPress} />
            </div>
          )}
          <div className='flex flex-col px-10 py-4 border-t border-gray-200'>
            <div className='mb-1 flex items-center'>
              <label className='capitalize inline-flex font-semibold text-tBlack'>Rep Duration</label>
              <Tooltip
                content='Duration of one repetition at a moderate pace'
                triggerClasses='ml-1 text-gray-500 hover:text-tGreen cursor-pointer inline-block'
              >
                <CgInfo className='w-4 h-4' />
              </Tooltip>
            </div>
            <Time
              name='time'
              value={formState.timeEstimatePerRep || '00:00:00.00'}
              setValue={(value) => setValue('timeEstimatePerRep', value)}
              inputClassNames='!py-2 !h-auto !text-sm !rounded-lg'
              minsRef={minsRef}
              secsRef={secsRef}
              onKeyUp={moveFocusOnKeyPress}
              timeTypes={['mins', 'secs']}
            />
          </div>
          <div className='flex flex-col px-10 py-4 border-t border-gray-200'>
            <label className='capitalize inline-flex font-semibold text-tBlack mb-1'>Measurement Units</label>
            <div className='flex items-center'>
              {formState.units.map((unit) => (
                <Tag
                  key={getAdjustedUnit(unit)}
                  text={getAdjustedUnit(unit)}
                  remove={() =>
                    setValue(
                      'units',
                      formState.units.filter((u) => getAdjustedUnit(u) !== getAdjustedUnit(unit))
                    )
                  }
                  css={tw`mb-0 bg-black text-white px-2.5 py-1.5`}
                />
              ))}
              {formState.units.length < 2 && (
                <AddUnitButton
                  units={formState.units}
                  handleSave={(units) => setValue('units', units)}
                  isPortalled={false}
                />
              )}
            </div>
          </div>
        </form>
      </div>
      {!isNewExercise && deleteConfirmation ? (
        <DeleteConfirmationBanner
          text='Are you sure?'
          handleDelete={handleDelete}
          handleGoBack={() => setDeleteConfirmation(false)}
          loading={loading}
        />
      ) : (
        <UpdateActions
          itemKey={exerciseId}
          handleSubmit={handleSubmit}
          handleDelete={() => setDeleteConfirmation(true)}
          loading={loading}
          hideDelete={isNewExercise}
          disabled={isFormDisabled}
          actionText={'Save'}
          form='exerciseForm'
          ref={submitRef}
          onKeyUp={(e) => {
            if (e.code === 'Tab' && e.shiftKey && !exLibEnabled && accordionValue !== 'Progressions') {
              maybeOpenAndFocusProgressions()
            } else {
              moveFocusOnKeyPress(e)
            }
          }}
          onKeyDown={(e) => {
            if (e.code === 'Tab' && !e.shiftKey) {
              // Timeout is needed because keyUp event is triggered in progressionName input
              // and progressionName input is skipped immediately after tabbing to it from submitBtn
              // e.stopPropagation and e.preventDefault does not prevent event from firing
              e.stopPropagation()
              setTimeout(() => {
                moveFocusedInputTo(0)
              }, 200)
            }
          }}
        />
      )}
    </FormProvider>
  )
}

const Label = tw.label`inline-flex cursor-pointer font-semibold text-tBlack`

const InputError = tw.p`flex text-xs mt-1 text-tRed`

const ExerciseNameInput = ({ register, inputRef, errors, onKeyDown, onKeyUp, onFocus, autoFocus }) => (
  <div className='mb-2'>
    <Label htmlFor='name' tw='mb-1'>
      Exercise name
    </Label>
    <Input
      name='name'
      type='text'
      placeholder='Enter exercise name'
      register={register}
      inputRef={inputRef}
      error={errors?.name?.message}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onFocus={onFocus}
      autoFocus={autoFocus}
    />
    {errors.name && <InputError>{errors.name.message}</InputError>}
  </div>
)

const CueInputAccordion = ({ accordionValue, setAccordionValue, copyCues, haveCues, inputRefsSortMethod }) => {
  useEffect(() => {
    if (!haveCues) {
      setAccordionValue('')
    }
  }, [haveCues, setAccordionValue])

  return (
    <Accordion.Root
      type='single'
      collapsible='true'
      value={accordionValue}
      onValueChange={setAccordionValue}
      className='border-b border-gray-200'
    >
      <Accordion.Item value='formCues'>
        <Accordion.Header>
          <Accordion.Trigger
            type='button'
            className='flex items-center w-full group px-4 md:px-10 py-4'
            tabIndex={-1}
            aria-label='Form cues'
          >
            <Label htmlFor='cue1' tw='my-2'>
              Form cues
            </Label>
            {haveCues ? (
              <>
                <Tooltip content='Copy all cues' triggerClasses='mr-2 ml-auto text-tBlack hover:text-tGreen'>
                  <FiCopy className='w-[18px] h-[18px]' onClick={copyCues} aria-label='copy-cues' />
                </Tooltip>
                <CgChevronDown className='w-6 h-6 group-radix-state-open:-rotate-180 transition-all' />
              </>
            ) : (
              <CgAdd className='w-[22px] h-[22px] group-radix-state-open:-rotate-180 transition-all text-tBlack ml-auto' />
            )}
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content>
          <div className='pb-4'>
            <CueInputList
              setAccordionValue={setAccordionValue}
              inputRefsSortMethod={inputRefsSortMethod}
              onEmptyInputKeyPress={() => setAccordionValue('Progressions')}
            />
          </div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  )
}

const LevelInput = ({ register, inputRef, errors, onKeyUp, onFocus }) => (
  <>
    <div className='flex items-center mt-3'>
      <Label htmlFor='level'>Level</Label>
      <Input
        name='level'
        type='number'
        register={register}
        inputRef={inputRef}
        placeholder='1-100'
        min={1}
        max={100}
        tw='h-10 w-[90px] ml-4'
        onKeyUp={onKeyUp}
        onFocus={onFocus}
        error={errors?.level?.message}
      />
    </div>
    {errors.level && <InputError>{errors.level.message}</InputError>}
  </>
)

export default ExerciseForm
